import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { type SearchParamsItemAction, updateAllQueryNFTs } from '@app/client/resources/yat-pages/page-items';
import {
    addSelectedItems,
    clearManagerSelection,
    handleRemoveItemsToYatPage,
    selectAllManagerItems,
    selectManagerItems,
    setNFTManagerPanelClose,
} from '@app/store/actions/nft-manager';
import { getYatPageDetails } from '@app/store/actions/yat-page';
import { addStagedLiquidIDItem, stageLiquidIDItem } from '@app/store/slices';
import { type NFTItem } from '@app/store/types/nft-manager';

import { getItemById } from '../utils';

import { useGetNfts, useNFTManagerSearchQuery } from './useGetNfts';

export const useHandleSelectItems = () => {
    const { data } = useGetNfts();
    const { tablet } = useMediaQuery();
    const { searchQuery } = useNFTManagerSearchQuery();

    const liquidIDSelectedItem = useAppSelector((state) => state.yatPage.liquidIDSelectedItem);
    const editingLiquidID = useAppSelector((state) => state.yatPage.editStates.editingLiquidID);
    const toggledAssets = useAppSelector((state) => state.yatPage.pageAssets.toggled);
    const liquidIDItems = useAppSelector((state) => state.yatPage.liquidIDItems);
    const pageId = useAppSelector((state) => state.yatPage.id);

    const selectedItems = useAppSelector((state) => state.nftManager.selectedItems);
    const selectedAllItems = useAppSelector((state) => state.nftManager.selectedAllItems);

    const user = useAppSelector((state) => state.user);
    const emojiID = user?.userAccount?.user.eid;
    const dispatch = useAppDispatch();

    const selectedCount = selectedAllItems ? data?.pages?.[0].paging.total || 0 : selectedItems.length;

    const isSelectedLID = useCallback(
        (item: NFTItem) =>
            liquidIDItems.some((liquidIdItem) => item.id === liquidIdItem?.id) || liquidIDSelectedItem?.id === item.id,
        [liquidIDSelectedItem, liquidIDItems],
    );

    const isSelectedPageItem = useCallback(
        (item: NFTItem) => selectedAllItems || selectedItems.some((selectedItem) => selectedItem == item.id),
        [selectedItems, selectedAllItems],
    );

    const handleIsSelected = useCallback(
        (item: NFTItem) => (editingLiquidID ? isSelectedLID(item) : isSelectedPageItem(item)),
        [isSelectedPageItem, isSelectedLID, editingLiquidID],
    );

    const handleSelectItems = useCallback(
        (item: NFTItem) => {
            if (!tablet && editingLiquidID) {
                dispatch(stageLiquidIDItem(item));
                return;
            } else if (editingLiquidID) {
                return;
            }

            if (selectedAllItems) {
                dispatch(clearManagerSelection());
            } else {
                const presentAndNotRemoved = item.content?.on_page && toggledAssets[item.id] !== 'removed';
                const inPage = presentAndNotRemoved || toggledAssets[item.id] === 'added';
                dispatch(selectManagerItems(item.id, inPage));
            }
        },
        [dispatch, editingLiquidID, tablet, selectedAllItems, toggledAssets],
    );

    const handleToggleSelectAllItems = () => {
        dispatch(selectAllManagerItems());
    };

    const onActionSelectedItems = useCallback(
        (action: SearchParamsItemAction) => {
            if (selectedAllItems && pageId && emojiID) {
                updateAllQueryNFTs({ ...searchQuery, pageId, action }).then(() => {
                    dispatch(setNFTManagerPanelClose());
                    dispatch(getYatPageDetails(emojiID, false, true));
                });
            } else {
                dispatch(clearManagerSelection());
                if (action === 'Add') {
                    const selectedItemsData = selectedItems.reduce<NFTItem[]>((collection, item) => {
                        const retrievedItem = data && getItemById(item, data);
                        if (retrievedItem) collection.push(retrievedItem);
                        return collection;
                    }, []);
                    dispatch(addSelectedItems(selectedItemsData));
                } else if (action === 'Remove') {
                    dispatch(handleRemoveItemsToYatPage(selectedItems));
                }
            }
        },
        [dispatch, selectedItems, data, pageId, searchQuery, selectedAllItems, emojiID],
    );

    const handleSelectedItemsAction = useCallback(
        (action: SearchParamsItemAction) => {
            if (liquidIDSelectedItem && editingLiquidID) {
                dispatch(addStagedLiquidIDItem());
                dispatch(setNFTManagerPanelClose());
            } else if (!editingLiquidID) {
                onActionSelectedItems(action);
            }
        },
        [liquidIDSelectedItem, editingLiquidID, dispatch, onActionSelectedItems],
    );

    return {
        handleSelectItems,
        handleIsSelected,
        selectedItems,
        handleToggleSelectAllItems,
        selectedAllItems,
        selectedCount,
        handleSelectedItemsAction,
    };
};
